/**
 * What a rejection from Dispatch (VSoft) or our own gateway MEANS, for the employee who hit it (owner, 2026-10-21:
 * «مفروض تطلع سبب الايرور لما يرفض عشان الناس تفهم كل ايرور وصل عندهم … عشان يفهموا واحنا نفهم»).
 *
 * Before, a rejection whose text was English read only «Dispatch رفض الطلب ورجع سبب الرفض» and the reason itself
 * («Can't insert DRS») sat hidden behind تفاصيل فنية. Each entry here matches the upstream text (English or Arabic)
 * and, where the same words mean different things, the endpoint; it says why it happens and what to do. A message
 * nobody has explained yet is still shown as it came — never swallowed.
 *
 * New messages: the gateway-ops action `error-messages` lists every distinct one of the last 30 days.
 */
export type ErrorSeverity = 'block' | 'warn' | 'retry'

export interface ErrorExplanation {
  title: string
  /** Why it happens, in a sentence an employee understands. */
  why: string
  /** What to do about it. */
  todo: string
  severity: ErrorSeverity
}

interface Entry extends ErrorExplanation {
  match: RegExp
  /** Only on these endpoints, when the same words mean something else elsewhere. */
  endpoint?: RegExp
}

const CATALOG: Entry[] = [
  {
    match: /can'?t insert drs/i,
    title: 'لا يمكن إضافة الشحنة إلى وثيقة توزيع جديدة',
    why: 'رفض النظام إضافة الشحنة إلى الوثيقة. غالبا لا تزال مرتبطة بوثيقة مفتوحة لمندوب آخر، أو أغلقت كتسليم جزئي ولم تعد من المندوب، أو أن حالتها لا تسمح بخروجها للتوزيع.',
    todo: 'استلمها كمرتجع من آخر مندوب كانت معه من «مرتجعات المناديب» ثم أعد إضافتها. وإذا كانت تسليما جزئيا، فاستخدم كود الجزء المرتجع الذي ينتهي باللاحقة -P.',
    severity: 'block',
  },
  {
    match: /can'?t insert|cannot insert|could not insert/i,
    title: 'رفض النظام تسجيل العملية',
    why: 'لم يقبل النظام حفظ هذا السجل، غالبا لأن الشحنة أو البيانات مرتبطة بعملية أخرى لا تزال مفتوحة.',
    todo: 'راجع حالة الشحنة وآخر عملية عليها قبل إعادة المحاولة. إذا تكرر الخطأ فأرسله للمعالجة.',
    severity: 'block',
  },
  {
    match: /لم يتم إرجاع أي بيانات|لم يتم ارجاع اي بيانات|no data (was )?returned|no records? found/i,
    title: 'لا توجد نتائج',
    why: 'لم يجد النظام أي بيانات تطابق هذا البحث.',
    todo: 'تأكد من الرقم أو الفلتر المحدد. وإذا لم تكن مع المندوب شحنات فهذا طبيعي.',
    severity: 'warn',
  },
  {
    match: /يوجد شحنة أو أكثر غير صالحة للتوزيع|غير صالحة للتوزيع/,
    title: 'توجد شحنة غير صالحة للتوزيع',
    why: 'حالة شحنة أو أكثر من الشحنات الممسوحة لا تسمح بخروجها مع مندوب: مسلمة، أو مرتجعة، أو في وثيقة أخرى، أو خارج الفرع.',
    todo: 'أزل الشحنة المميزة بعلامة حمراء وأكمل بالباقي، أو راجع حالتها في الاستعلام.',
    severity: 'block',
  },
  {
    match: /invalid awbs?|awb(s)? (is |are )?invalid|كود غير صحيح|رقم الشحنة غير صحيح/i,
    title: 'رقم الشحنة غير مقبول لهذه العملية',
    why: 'رقم الشحنة غير صحيح، أو حالة الشحنة الحالية لا تسمح بهذه العملية (مثل إعادة تشغيل شحنة غير مغلقة).',
    todo: 'تأكد من الكود ومن حالة الشحنة الحالية في الاستعلام قبل إعادة المحاولة.',
    severity: 'block',
  },
  {
    match: /already exists?|duplicate|موجود (بالفعل|مسبقا)|مكرر/i,
    title: 'هذا السجل موجود مسبقا',
    why: 'يوجد سجل مسجل بالفعل بنفس الكود أو نفس البيانات، لذلك لا يقبل النظام تكراره.',
    todo: 'ابحث عن السجل الموجود واعمل عليه، أو استخدم كودا آخر.',
    severity: 'block',
  },
  {
    match: /quota exceeded|too many requests|rate.?limit/i,
    title: 'النظام مشغول حاليا',
    why: 'أرسلت إلى النظام طلبات كثيرة في الوقت نفسه، فأوقف الباقي لثوان.',
    todo: 'انتظر بضع ثوان ثم أعد المحاولة.',
    severity: 'retry',
  },
  {
    match: /<html|<!doctype|iis|404 - file or directory not found|server error in '\/' application/i,
    title: 'أعاد النظام صفحة خاطئة بدلا من البيانات',
    why: 'أعاد خادم النظام صفحة ويب بدلا من الرد المعتاد، ويحدث ذلك عندما يكون الخادم متوقفا أو قيد إعادة التشغيل.',
    todo: 'انتظر دقيقة ثم أعد المحاولة. إذا استمرت المشكلة فأرسل الخطأ للمعالجة.',
    severity: 'retry',
  },
  {
    match: /timeout|timed out|ETIMEDOUT|ECONNRESET|ECONNREFUSED|fetch failed|socket hang up|bad gateway|gateway time-?out|service unavailable/i,
    title: 'النظام لا يستجيب',
    why: 'وصل الطلب إلى النظام ولم يصل رد في الوقت المحدد، أو انقطع الاتصال به.',
    todo: 'راجع الشاشة أولا لأن العملية ربما نفذت، ثم أعد المحاولة.',
    severity: 'retry',
  },
  {
    match: /you do not have permission|forbidden|not authorized|غير مصرح/i,
    title: 'ليست لديك صلاحية لهذه العملية',
    why: 'الصلاحيات الممنوحة لحسابك لا تسمح بهذه العملية.',
    todo: 'تواصل مع المسؤول لمنحك الصلاحية.',
    severity: 'block',
  },
  {
    match: /unauthori[sz]ed|token (is )?(expired|invalid)|session (expired|ended)|انتهت الجلسة/i,
    title: 'انتهت الجلسة',
    why: 'انتهت مدة تسجيل الدخول الخاص بك.',
    todo: 'سجل الدخول مرة أخرى ثم أكمل.',
    severity: 'block',
  },
  {
    match: /one or more validation errors|validation (failed|error)|is required|field .* required|must be/i,
    title: 'توجد بيانات ناقصة أو غير صحيحة',
    why: 'وجد النظام حقلا مطلوبا فارغا أو قيمة بتنسيق غير صحيح.',
    todo: 'راجع الحقول المطلوبة في الشاشة وصححها.',
    severity: 'block',
  },
]

/** The explanation of one upstream / gateway message, or `null` when nobody has explained it yet. */
export function explainUpstream(message: string, endpoint = ''): ErrorExplanation | null {
  const text = String(message ?? '')
  if (!text.trim()) return null
  const hit = CATALOG.find((e) => e.match.test(text) && (!e.endpoint || e.endpoint.test(endpoint)))
  if (!hit) return null
  const { match: _m, endpoint: _e, ...out } = hit
  return out
}

/** The explanation for an HTTP status alone, when the message says nothing (permission from `details.required`). */
export function explainStatus(status: number, required?: string | string[]): ErrorExplanation | null {
  if (status === 403) {
    const need = (Array.isArray(required) ? required : required ? [required] : []).join('، ')
    return {
      title: 'ليست لديك صلاحية لهذه العملية',
      why: need ? `تتطلب هذه العملية صلاحية ${need} وهي غير مفعلة على حسابك.` : 'الصلاحيات الممنوحة لحسابك لا تسمح بهذه العملية.',
      todo: 'تواصل مع المسؤول لمنحك هذه الصلاحية من مجموعات الصلاحيات.',
      severity: 'block',
    }
  }
  if (status === 401) return { title: 'انتهت الجلسة', why: 'انتهت مدة تسجيل الدخول الخاص بك.', todo: 'سجل الدخول مرة أخرى ثم أكمل.', severity: 'block' }
  if (status === 429) return { title: 'النظام مشغول حاليا', why: 'أرسلت طلبات كثيرة في الوقت نفسه، فأوقف الباقي لثوان.', todo: 'انتظر بضع ثوان ثم أعد المحاولة.', severity: 'retry' }
  if (status === 502 || status === 503 || status === 504) return { title: 'النظام لا يستجيب', why: 'الخدمة المسؤولة عن الطلب لا تستجيب في الوقت المحدد.', todo: 'انتظر قليلا ثم أعد المحاولة.', severity: 'retry' }
  return null
}

/** «UPSTREAM_REJECTED: Can't insert DRS» → «Can't insert DRS»: the gateway's code prefix is not part of the reason. */
export const stripCode = (s: string) => String(s ?? '').replace(/^[A-Z][A-Z_]{3,}:\s*/, '').trim()
